import ReportSectionCard from './shared/ReportSectionCard.component'
import ReportSectionHeading from '@components/pages/Reports/shared/ReportSectionHeading.component'
import React, { useMemo, useState } from 'react'
import ReportViewTabs from '@components/pages/Reports/shared/ReportViewTabs.component'
import DashboardSectionEmptyState from '@components/pages/Dashboard/DashboardSectionEmptyState.component'
import ReportBundleStickyTopWhenExpanded from '@components/pages/Reports/shared/ReportBundleStickyTopWhenExpanded.component'
import ReportFilterToolbar from '@components/pages/Reports/shared/ReportFilterToolbar.component'
import ReportKpiGrid from '@components/pages/Reports/shared/ReportKpiGrid.component'
import ReportPageHeader from '@components/pages/Reports/shared/ReportPageHeader.component'
import { REPORTS_DISTRIBUTION_VIEW_TABS } from '@constants/Reports/patientBaseHealth.constants'
import {
  INSURANCE_PIE_COLOR,
  PATIENT_PIE_COLOR,
  REVENUE_REALIZATION_FILTERS,
  REVENUE_REALIZATION_KPI_DEFINITIONS,
} from '@constants/Reports/revenueRealizationAnalytics.constants'
import {
  ReportsDistributionView,
  ReportsKpiMetric,
} from '@dataTypes/reports.type'
import { useRevenueRealizationFinance } from '@hooks/reports/useRevenueRealizationFinance'
import { useReportFinanceUiEffects } from '@hooks/reports/useReportFinanceUiEffects'
import {
  hasNonZeroDashboardValues,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import {
  computeDynamicDollarAxis,
  computePercentAxis,
  formatKpiCurrency,
  formatKpiPercent,
  mergeProductionCollectionsByPeriod,
  toRelativePercentPair,
} from '@utils/reports/revenueRealizationFinance.util'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from '@components/pages/Reports/shared/ReportDesignSystemCharts.component'

const REPORT_CHART_AXIS_TICK = { fontSize: 12, fill: 'var(--gray-500)' }
const REPORT_CHART_TOOLTIP_STYLE = {
  borderRadius: 8,
  border: '1px solid var(--gray-200)',
  fontSize: 12,
}
const REPORT_CHART_GRID_STROKE = 'var(--gray-100)'
const REPORT_BAR_CHART_MARGIN = { top: 24, right: 12, left: 4, bottom: 0 }
const REPORT_BAR_CHART_MARGIN_LEGEND = {
  top: 24,
  right: 12,
  left: 4,
  bottom: 0,
}
const REPORT_LEGEND_WRAPPER_STYLE = { paddingTop: 0, fontSize: 12 }
const REPORT_BAR_CATEGORY_GAP = '16%'
const REPORT_GROUPED_BAR_GAP = 4
const REPORT_YAXIS_WIDTH = 52

function formatDollarLabel(value: number): string {
  if (value === 0) return '$0'
  return `$${Math.round(value / 1000)}K`
}

function formatDollarTooltip(value: number): string {
  return `$${value.toLocaleString('en-US')}`
}

function RevenueRealizationAnalyticsReport(): React.ReactElement {
  const [productionView, setProductionView] =
    useState<ReportsDistributionView>('Count')
  const [reportSaveViewAction, setReportSaveViewAction] =
    useState<JSX.Element | null>(null)
  const [reportGeneratePDFAction, setReportGeneratePDFAction] =
    useState<JSX.Element | null>(null)
  // `reportFiltersAndMetrics` is the source of truth for the filters and metrics of the currently selected report view.
  // Use this for all data queries and any logic that depends on the selected report view.
  const [reportFiltersAndMetrics, setReportFiltersAndMetrics] = useState<{
    filters: Record<string, boolean>
    metrics: Record<string, boolean>
  } | null>(null)
  const [relatedCollectionView, setRelatedCollectionView] =
    useState<ReportsDistributionView>('Count')
  const { loading, error, data, bucketLabel } = useRevenueRealizationFinance(
    (reportFiltersAndMetrics?.filters ?? {}) as unknown as Record<
      string,
      string
    >,
  )
  useReportFinanceUiEffects({
    loading,
    error,
    errorMessage: 'Failed to load revenue realization data. Please try again.',
  })

  const kpiItems = useMemo((): ReportsKpiMetric[] => {
    if (!data) {
      return REVENUE_REALIZATION_KPI_DEFINITIONS.map((def) => ({
        id: def.id,
        title: def.title,
        footer: def.footer,
        value: loading ? '…' : '—',
      }))
    }

    const valueById: Record<string, number> = {
      'total-production': data.totalProduction.value,
      'total-collections': data.totalCollections.value,
      'net-collection': data.netCollection.value,
      'gross-collection': data.grossCollection.value,
      'same-day-collections': data.sameDayCollections.value,
      'adjustment-rate': data.adjustmentRate.value,
    }

    return REVENUE_REALIZATION_KPI_DEFINITIONS.map((def) => ({
      id: def.id,
      title: def.title,
      footer: def.footer,
      value:
        def.format === 'currency'
          ? formatKpiCurrency(valueById[def.id])
          : formatKpiPercent(valueById[def.id]),
    }))
  }, [data, loading])

  const productionVsCollectionsData = useMemo(() => {
    if (!data) return []

    const merged = mergeProductionCollectionsByPeriod(
      data.production.buckets,
      data.collection.buckets,
    )

    if (productionView === 'Count') {
      return merged.map((row) => ({
        period: row.period,
        production: row.production,
        collections: row.collections,
        productionLabel: formatDollarLabel(row.production),
        collectionsLabel: formatDollarLabel(row.collections),
      }))
    }

    return merged.map((row) => {
      const pct = toRelativePercentPair(row.production, row.collections)
      return {
        period: row.period,
        production: pct.production,
        collections: pct.collections,
        productionLabel: `${pct.production}%`,
        collectionsLabel: `${pct.collections}%`,
      }
    })
  }, [data, productionView])

  const productionYAxis = useMemo(() => {
    if (productionView === 'Percentage') {
      return {
        domain: [0, 100] as [number, number],
        ticks: [0, 20, 40, 60, 80, 100],
        tickFormatter: (v: number) => `${v}%`,
      }
    }

    const values = productionVsCollectionsData.flatMap((row) => [
      row.production,
      row.collections,
    ])
    return computeDynamicDollarAxis(values)
  }, [productionView, productionVsCollectionsData])

  const productionVsRelatedCollectionsData = useMemo(() => {
    if (!data) return []

    const merged = mergeProductionCollectionsByPeriod(
      data.production.buckets,
      data.collectionForProductionOfPeriod.buckets,
    )

    if (relatedCollectionView === 'Count') {
      return merged.map((row) => ({
        period: row.period,
        production: row.production,
        collections: row.collections,
        productionLabel: formatDollarLabel(row.production),
        collectionsLabel: formatDollarLabel(row.collections),
      }))
    }

    return merged.map((row) => {
      const pct = toRelativePercentPair(row.production, row.collections)
      return {
        period: row.period,
        production: pct.production,
        collections: pct.collections,
        productionLabel: `${pct.production}%`,
        collectionsLabel: `${pct.collections}%`,
      }
    })
  }, [data, relatedCollectionView])

  const relatedProductionYAxis = useMemo(() => {
    if (relatedCollectionView === 'Percentage') {
      return {
        domain: [0, 100] as [number, number],
        ticks: [0, 20, 40, 60, 80, 100],
        tickFormatter: (v: number) => `${v}%`,
      }
    }

    const values = productionVsRelatedCollectionsData.flatMap((row) => [
      row.production,
      row.collections,
    ])
    return computeDynamicDollarAxis(values)
  }, [relatedCollectionView, productionVsRelatedCollectionsData])

  const netCollectionPercentData = useMemo(() => {
    if (!data) return []
    return data.netCollectionPercentByPeriod.buckets.map((row) => ({
      period: row.label,
      barValue: row.value,
      topLabel: `${row.value}%`,
    }))
  }, [data])

  const netCollectionPercentYAxis = useMemo(() => {
    const values = netCollectionPercentData.map((row) => row.barValue)
    return computePercentAxis(values)
  }, [netCollectionPercentData])

  const adjustmentRateData = useMemo(() => {
    if (!data) return []
    return data.adjustmentRateByPeriod.buckets.map((row) => ({
      period: row.label,
      barValue: row.value,
      topLabel: `${row.value}%`,
    }))
  }, [data])

  const adjustmentRateYAxis = useMemo(() => {
    const values = adjustmentRateData.map((row) => row.barValue)
    return computePercentAxis(values)
  }, [adjustmentRateData])

  const pieData = useMemo(() => {
    if (!data) return []
    return [
      {
        name: 'Insurance',
        value: data.insuranceVsPatient.insurance.percentage,
        color: INSURANCE_PIE_COLOR,
      },
      {
        name: 'Patient',
        value: data.insuranceVsPatient.patient.percentage,
        color: PATIENT_PIE_COLOR,
      },
    ]
  }, [data])

  const pieLegend = useMemo(() => {
    if (!data) {
      return [
        {
          id: 'insurance',
          label: 'Insurance',
          percentage: 0,
          color: INSURANCE_PIE_COLOR,
        },
        {
          id: 'patient',
          label: 'Patient',
          percentage: 0,
          color: PATIENT_PIE_COLOR,
        },
      ]
    }
    return [
      {
        id: 'insurance',
        label: 'Insurance',
        percentage: data.insuranceVsPatient.insurance.percentage,
        color: INSURANCE_PIE_COLOR,
      },
      {
        id: 'patient',
        label: 'Patient',
        percentage: data.insuranceVsPatient.patient.percentage,
        color: PATIENT_PIE_COLOR,
      },
    ]
  }, [data])

  const renderPercentChart = (
    chartData: Array<{ period: string; barValue: number; topLabel: string }>,
    axis: {
      domain: [number, number]
      ticks: number[]
      tickFormatter: (v: number) => string
    },
  ): React.ReactElement => (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={chartData}
        margin={REPORT_BAR_CHART_MARGIN}
        barCategoryGap={REPORT_BAR_CATEGORY_GAP}
      >
        <CartesianGrid
          vertical={false}
          strokeDasharray="0"
          stroke={REPORT_CHART_GRID_STROKE}
        />
        <XAxis
          dataKey="period"
          axisLine={false}
          tickLine={false}
          tick={REPORT_CHART_AXIS_TICK}
        />
        <YAxis
          domain={axis.domain}
          ticks={axis.ticks}
          tickFormatter={axis.tickFormatter}
          axisLine={false}
          tickLine={false}
          tick={REPORT_CHART_AXIS_TICK}
          width={REPORT_YAXIS_WIDTH}
        />
        <Tooltip
          formatter={(value: number) => `${value}%`}
          contentStyle={REPORT_CHART_TOOLTIP_STYLE}
        />
        <Bar
          dataKey="barValue"
          fill="var(--blue-400)"
          radius={[8, 8, 8, 8]}
          barSize={100}
        />
      </BarChart>
    </ResponsiveContainer>
  )

  const kpiEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasNonZeroDashboardValues(
      data?.totalProduction.value,
      data?.totalCollections.value,
      data?.netCollection.value,
      data?.grossCollection.value,
      data?.sameDayCollections.value,
      data?.adjustmentRate.value,
    ),
  )
  const productionEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.production?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ) ||
      (data?.collection?.buckets ?? []).some((row) =>
        hasNonZeroDashboardValues(row.value),
      ),
  )
  const relatedEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.production?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ) ||
      (data?.collectionForProductionOfPeriod?.buckets ?? []).some((row) =>
        hasNonZeroDashboardValues(row.value),
      ),
  )
  const netCollectionEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.netCollectionPercentByPeriod?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )
  const adjustmentEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.adjustmentRateByPeriod?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )
  const pieEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasNonZeroDashboardValues(
      data?.insuranceVsPatient?.insurance.amount,
      data?.insuranceVsPatient?.insurance.percentage,
      data?.insuranceVsPatient?.patient.amount,
      data?.insuranceVsPatient?.patient.percentage,
    ),
  )

  return (
    <div className="reports-patient-base-health reports-treatment-conversion-funnel reports-revenue-realization-analytics">
      <ReportBundleStickyTopWhenExpanded>
        <ReportPageHeader title="Revenue Realization Analytics">
          {reportSaveViewAction}
          {reportGeneratePDFAction}
        </ReportPageHeader>

        <ReportFilterToolbar
          filters={REVENUE_REALIZATION_FILTERS}
          onReportSaveViewActionChange={setReportSaveViewAction}
          onReportGeneratePDFActionChange={setReportGeneratePDFAction}
          onReportFiltersAndMetricsChange={setReportFiltersAndMetrics}
        />
      </ReportBundleStickyTopWhenExpanded>

      {kpiEmpty ? (
        <DashboardSectionEmptyState />
      ) : (
        <ReportKpiGrid items={kpiItems} />
      )}

      <div className="reports-treatment-conversion-funnel__stack">
        <div className="reports-treatment-conversion-funnel__grid">
          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Production vs Collections ({bucketLabel})
                </ReportSectionHeading>
                {productionEmpty ? null : (
                  <ReportViewTabs
                    options={REPORTS_DISTRIBUTION_VIEW_TABS}
                    value={productionView}
                    onChange={(value) =>
                      setProductionView(value as ReportsDistributionView)
                    }
                    className="reports-patient-base-health__view-group"
                  />
                )}
              </div>
              {productionEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={productionVsCollectionsData}
                      margin={REPORT_BAR_CHART_MARGIN_LEGEND}
                      barSize={50}
                      barGap={REPORT_GROUPED_BAR_GAP}
                      barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                    >
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="0"
                        stroke={REPORT_CHART_GRID_STROKE}
                      />
                      <XAxis
                        dataKey="period"
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                      />
                      <YAxis
                        domain={productionYAxis.domain}
                        ticks={productionYAxis.ticks}
                        tickFormatter={productionYAxis.tickFormatter}
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                        width={REPORT_YAXIS_WIDTH}
                      />
                      <Tooltip
                        formatter={(value: number) =>
                          productionView === 'Count'
                            ? formatDollarTooltip(value)
                            : `${value}%`
                        }
                        contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                      />
                      <Legend
                        iconType="circle"
                        iconSize={8}
                        wrapperStyle={REPORT_LEGEND_WRAPPER_STYLE}
                      />
                      <Bar
                        dataKey="production"
                        name="Production"
                        fill="var(--blue-400)"
                        radius={[8, 8, 8, 8]}
                      />
                      <Bar
                        dataKey="collections"
                        name="Collections"
                        fill="var(--orange-300)"
                        radius={[8, 8, 8, 8]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </ReportSectionCard>

          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Production vs Related Collections ({bucketLabel})
                </ReportSectionHeading>
                {relatedEmpty ? null : (
                  <ReportViewTabs
                    options={REPORTS_DISTRIBUTION_VIEW_TABS}
                    value={relatedCollectionView}
                    onChange={(value) =>
                      setRelatedCollectionView(value as ReportsDistributionView)
                    }
                    className="reports-patient-base-health__view-group"
                  />
                )}
              </div>
              {relatedEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={productionVsRelatedCollectionsData}
                      margin={REPORT_BAR_CHART_MARGIN_LEGEND}
                      barSize={50}
                      barGap={REPORT_GROUPED_BAR_GAP}
                      barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                    >
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="0"
                        stroke={REPORT_CHART_GRID_STROKE}
                      />
                      <XAxis
                        dataKey="period"
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                      />
                      <YAxis
                        domain={relatedProductionYAxis.domain}
                        ticks={relatedProductionYAxis.ticks}
                        tickFormatter={relatedProductionYAxis.tickFormatter}
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                        width={REPORT_YAXIS_WIDTH}
                      />
                      <Tooltip
                        formatter={(value: number) =>
                          relatedCollectionView === 'Count'
                            ? formatDollarTooltip(value)
                            : `${value}%`
                        }
                        contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                      />
                      <Legend
                        iconType="circle"
                        iconSize={8}
                        wrapperStyle={REPORT_LEGEND_WRAPPER_STYLE}
                      />
                      <Bar
                        dataKey="production"
                        name="Production"
                        fill="var(--blue-400)"
                        radius={[8, 8, 8, 8]}
                      />
                      <Bar
                        dataKey="collections"
                        name="Related collections"
                        fill="var(--orange-300)"
                        radius={[8, 8, 8, 8]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </ReportSectionCard>
        </div>

        <div className="reports-treatment-conversion-funnel__grid">
          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Net Collection rate ({bucketLabel})
                </ReportSectionHeading>
              </div>
              {netCollectionEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  {renderPercentChart(
                    netCollectionPercentData,
                    netCollectionPercentYAxis,
                  )}
                </div>
              )}
            </div>
          </ReportSectionCard>

          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Adjustment rate ({bucketLabel})
                </ReportSectionHeading>
              </div>
              {adjustmentEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  {renderPercentChart(adjustmentRateData, adjustmentRateYAxis)}
                </div>
              )}
            </div>
          </ReportSectionCard>
        </div>

        <div className="reports-treatment-conversion-funnel__grid reports-revenue-realization-analytics__split-grid">
          <ReportSectionCard className="reports-patient-base-health__chart-card reports-revenue-realization-analytics__insurance-patient-payments-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Insurance vs patient payments
                </ReportSectionHeading>
              </div>
              {pieEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-revenue-realization-analytics__pie-body">
                  <div className="reports-revenue-realization-analytics__pie-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Tooltip
                          formatter={(value: number) => `${value}%`}
                          contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                        />
                        <Pie
                          data={pieData}
                          dataKey="value"
                          nameKey="name"
                          innerRadius={0}
                          outerRadius="100%"
                          paddingAngle={0}
                          startAngle={90}
                          endAngle={-270}
                          labelLine={false}
                        >
                          {pieData.map((entry) => (
                            <Cell
                              key={entry.name}
                              fill={entry.color}
                              stroke="var(--gray-white)"
                              strokeWidth={2}
                            />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <ul className="reports-revenue-realization-analytics__legend">
                    {pieLegend.map((slice) => (
                      <li
                        key={slice.id}
                        className="reports-revenue-realization-analytics__legend-row"
                      >
                        <span className="reports-revenue-realization-analytics__legend-label-group">
                          <span
                            className="reports-revenue-realization-analytics__legend-swatch"
                            style={{ backgroundColor: slice.color }}
                          />
                          <span className="reports-revenue-realization-analytics__legend-label">
                            {slice.label}
                          </span>
                        </span>
                        <span
                          className="reports-revenue-realization-analytics__legend-leader"
                          aria-hidden
                        />
                        <span className="reports-revenue-realization-analytics__legend-values">
                          <strong className="reports-revenue-realization-analytics__legend-value">
                            {loading ? '…' : `${slice.percentage}%`}
                          </strong>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </ReportSectionCard>
        </div>
      </div>
    </div>
  )
}

export default RevenueRealizationAnalyticsReport
